/**
 * Contains detailed information about a digital certificate.
 */
export interface CertificateInfo {
    /** Base64 encoded certificate data */
    certificate: string;
    /** The certificate authority that issued the certificate */
    issuer: string;
    /** The entity to whom the certificate was issued */
    subject: string;
    /** Array of certificate usage purposes */
    certificateUsage: string[];
    /** Date from which the certificate is valid (ISO format) */
    ValidFrom: string;
    /** Date until which the certificate is valid (ISO format) */
    ValidTo: string;
    /** Unique serial number of the certificate */
    serialNumber: string;
    /** Algorithm used for the certificate signature */
    signatureAlgorithm: string;
}


export interface NativeResponse {
    /** Contains the signed document content */
    document: {
        /** Base64 encoded document content */
        content: string;
    };
    /** Timestamp of when the signature was applied */
    timestamp: string;
    /** Details of the certificate used for signing */
    certificateDetails: CertificateInfo;
    /** Whether the operation was successful */
    status: boolean;
    /** Expiration time of the authentication token */
    tokenExpiry: string;
    /** Error information if the operation failed */
    error: {
        /** Error message */
        message: string;
    };
    /** Version of the signing service */
    version: string;
};

/**
 * Represents the position and dimensions of a signature on a document.
 */
export interface SignaturePosition {
    /** Identifier for the signature */
    sign: number;
    /** X-coordinate of the signature on the page */
    x: number;
    /** Y-coordinate of the signature on the page */
    y: number;
    /** Width of the signature area */
    width: number;
    /** Height of the signature area */
    height: number;
}

export interface SignaturesOnPage {
    /** The page number within the document */
    pageNumber: number;
    /** Array of signature positions on this page */
    signatures: SignaturePosition[];
}


export enum TextPosition {
    /** Position above the reference text */
    ABOVE = "above",
    /** Position below the reference text */
    BELOW = "below",
}

/**
 * Configuration for placing text in a document.
 */
export interface TextPlacement {
    /** Specific page numbers to place the text on */
    pages?: number[];
    /** Text to search for in the document to position relative to */
    searchText: string;
    /** Width of the text area */
    width: number;
    /** Height of the text area */
    height: number;
    /** Position relative to the search text */
    position?: TextPosition;
    /** Space between the search text and the placed content */
    gap?: number;
}

/**
 * Enum defining options for how a signer's name appears in the signature.
 */
export enum SignerNameText {
    /** Display only the signer's name */
    NAME_ONLY = "name-only",
    /** Display "Signed by" followed by the name */
    SIGNED_BY_WITH_NAME = "signed-by-name",
    /** Display "Signed by: [Signer's Name]" */
    SIGNED_BY_SIGNER_NAME = "signed-by-signer-name",
    /** Do not display the signer's name */
    NONE = "none",
}

export interface SignatureAppearance {
    /** Whether to display the timestamp in the signature */
    showTimestamp: boolean;
    /** Whether to display the validity icon in the signature */
    showValidityIcon: boolean;
    /** Base64 encoded image to display in the signature */
    image?: string;
    /** Configuration for how the signer's name appears */
    signerNameText?: SignerNameText;
    /** Custom text to display in the signature */
    customText?: string;
    /** Reason for signing */
    reason?: string;
    /** Location where the signing took place */
    location?: string;
    /** Entity authorizing the signature */
    authorizedBy?: string;
    /** Name of the signer */
    signerName?: string;
}


/**
 * Verifies if the current SignBridge token is valid and authorized
 *
 * Makes a request to the SignBridge Native service to validate the API token
 * configured in the environment variables.
 *
 * @returns Promise<NativeResponse> with token validation status and details
 * @property {boolean} status - true if token is valid, false otherwise
 * @property {string} [error] - Error message if validation failed
 */
export async function checkToken() {
    try {
        const response = await fetch("https://localhost:53000/check-token", {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "X-API-Token": process.env.VITE_PUBLIC_SIGNER_TOKEN || '',
            },
        });
        const data = (await response.json()) as NativeResponse;
        console.log("", 12345, { data });
        data.status = true;
        return data;
    } catch (error) {
        console.error("Native connection error:", error);
        return { status: false, error: "Failed to connect to SignSecure Native" };
    }
}

/**
 * Signs a PDF document with signatures placed on specific pages
 *
 * Uses the SignBridge Native service to digitally sign a PDF document with
 * signatures placed at the coordinates specified for each page.
 *
 * @param pages - Array of SignaturesOnPage objects defining signature placement coordinates on specific pages
 * @param base64Pdf - Base64 encoded PDF data to be signed
 * @param appearance - Visual appearance settings for the signature (dimensions, visibility, etc.)
 * @returns Promise<NativeResponse> with signing result
 * @property {boolean} status - true if signing was successful
 * @property {string} [error] - Error message if signing failed
 * @property {string} [data] - Base64 encoded signed PDF data
 */
export async function signWithPages(
    pages: SignaturesOnPage[],
    base64Pdf: string,
    appearance: SignatureAppearance
) {
    console.log({ p: process.env.VITE_PUBLIC_SIGNER_TOKEN, base64Pdf });

    const response = await fetch("https://localhost:53000/sign-pdf", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "X-API-Token": process.env.VITE_PUBLIC_SIGNER_TOKEN || '0cdEJI34kk3JJodab7W6xGv/6eZVW0Uun8BrRPOKA1i9hAyQGqPrWiGpGgxOtWc/qaOuIVVndihn1MLMo6VXkHv/mtwQdn+DoQ9ngCm1ZJoPTL8NQAM/q6KjsmGICXo=',
        },
        body: JSON.stringify({
            document: {
                source: {
                    type: "base64",
                    content: base64Pdf,
                },
                output: {
                    type: "base64",
                },
            },
            signature: {
                placement: {
                    type: "pages",
                    pages: pages,
                },
                appearance,
            },
        }),
    });
    return (await response.json()) as NativeResponse;
}


/**
 * Converts a PDF ArrayBuffer to a Base64 encoded string
 *
 * @param pdfBuffer - The PDF file as an ArrayBuffer
 * @returns Base64 encoded string representation of the PDF
 */
export const convertPdfBufferToBase64 = (pdfBuffer: ArrayBuffer) => {
    // Convert ArrayBuffer to Uint8Array
    const uint8Array = new Uint8Array(pdfBuffer);

    let binaryString = "";
    uint8Array.forEach((byte) => {
        binaryString += String.fromCharCode(byte);
    });

    const base64String = btoa(binaryString);

    return base64String;
};

export function base64ToBlob(base64: string, contentType = 'application/pdf') {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: contentType });
}



/**
 * Signs a PDF document with signatures placed relative to text content
 *
 * Uses the SignBridge Native service to digitally sign a PDF document with
 * signatures placed relative to specific text found in the document.
 *
 * @param textPlacement - Configuration object for signature placement relative to text
 *                        including search text, occurrence number, and positioning offsets
 * @param base64Pdf - Base64 encoded PDF data to be signed
 * @param appearance - Visual appearance settings for the signature (dimensions, visibility, etc.)
 * @returns Promise<NativeResponse> with signing result
 * @property {boolean} status - true if signing was successful
 * @property {string} [error] - Error message if signing failed
 * @property {string} [data] - Base64 encoded signed PDF data
 */
export async function signWithTextPlacement(
    textPlacement: TextPlacement,
    base64Pdf: string,
    appearance: SignatureAppearance
) {
    const response = await fetch("https://localhost:53000/sign-pdf", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "X-API-Token": '0cdEJI34kk3JJodab7W6xGv/6eZVW0Uun8BrRPOKA1i9hAyQGqPrWiGpGgxOtWc/qaOuIVVndihn1MLMo6VXkHv/mtwQdn+DoQ9ngCm1ZJoPTL8NQAM/q6KjsmGICXo=',
        },
        body: JSON.stringify({
            document: {
                source: {
                    type: "base64",
                    content: base64Pdf,
                },
                output: {
                    type: "base64",
                },
            },
            signature: {
                placement: {
                    type: "text",
                    textLocation: textPlacement,
                },
                appearance,
            },
        }),
    });
    return (await response.json()) as NativeResponse;
}